import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { CheckCircle, BarChart3, Zap } from "lucide-react";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        if (response.data) navigate('/dashboard');
      } catch (error) {
        // Not authenticated
      } finally {
        setChecking(false);
      }
    };
    checkAuth();
  }, [navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-sage-500 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-stone-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  const features = [
    { icon: Zap, text: "AI-powered invoice data extraction" },
    { icon: BarChart3, text: "Concurrent Drive folder scans" },
    { icon: CheckCircle, text: "Household order dashboards" },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full mx-4"
      >
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-stone-200/60 p-8">
          <div className="text-center mb-8">
            <img src="/logo.png" alt="Tell M" className="w-14 h-14 rounded-2xl mb-4 shadow-lg" />
            <h1 className="text-3xl font-heading font-extrabold text-stone-900 mb-2">Tell M <span className="text-stone-400 font-medium">|</span> <span className="text-stone-600 font-semibold">Invoices</span></h1>
            <p className="text-stone-500 text-sm">Automate your invoice & receipt processing</p>
          </div>

          <div className="space-y-3 mb-8">
            {features.map((feat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + i * 0.1 }}
                className="flex items-center gap-3"
              >
                <div className="w-8 h-8 bg-sage-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <feat.icon className="w-4 h-4 text-sage-600" />
                </div>
                <p className="text-sm text-stone-600">{feat.text}</p>
              </motion.div>
            ))}
          </div>

          <Button
            onClick={handleLogin}
            data-testid="login-button"
            className="w-full bg-gradient-to-r from-sage-500 to-sage-600 hover:from-sage-600 hover:to-sage-700 text-white py-6 rounded-xl font-semibold text-base transition-all shadow-lg shadow-sage-500/20 hover:shadow-sage-500/30"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Sign in with Google
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
